import { Link } from "react-router-dom";
import { SignupLink } from "@/pages/landing-signup-link";
import { DealerDogBrand } from "@/components/layout/dealerdog-brand";
import { CALENDLY_URL } from "@/lib/site-links";

type LandingSectionsProps = {
  dealerCount: string;
  year: number;
};

export function LandingSections({ dealerCount, year }: LandingSectionsProps) {
  return (
    <>

    <section className="hero" id="home">
      <div className="dd-container hero-grid">
        <div className="hero-copy">
          <div className="trust-pill">
            <span className="trust-pill-dot" aria-hidden="true"></span>
            Trusted by {dealerCount}+ dealers and vendors nationwide
          </div>

          <h1>
            Turn more shoppers into{" "}
            <span className="accent">funded buyers.</span>
          </h1>

          <p className="hero-subtitle">
            DealerDog gives equipment dealers fast financing direction at the point of sale, so your team can present a path forward while the customer is still ready to buy.
          </p>

          <div className="button-row">
            <SignupLink className="button button-primary">
              Create an Account
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m9 18 6-6-6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </SignupLink>

            <a className="button button-secondary" href={CALENDLY_URL}>
              Schedule a Demo
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M7 3v3M17 3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </a>
          </div>

          <div className="hero-notes" aria-label="DealerDog highlights">
            <span className="hero-note">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Soft-credit prequalification
            </span>
            <span className="hero-note">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Dealer-first workflow
            </span>
            <span className="hero-note">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Human financing support
            </span>
          </div>
        </div>

        <div className="product-stage" aria-label="DealerDog platform preview">
          <div className="stage-glow"></div>

          <div className="dashboard">
            <div className="dashboard-top">
              <div className="dash-brand">
                <span className="dash-brand-icon">DD</span>
                DealerDog Portal
              </div>
              <div className="dash-user">
                <span>Northwest Equipment</span>
                <span className="dash-avatar">NE</span>
              </div>
            </div>

            <div className="dashboard-body">
              <aside className="dash-sidebar" aria-hidden="true">
                <div className="dash-nav-item active" data-short="Home"><span className="dash-nav-dot"></span>Dashboard</div>
                <div className="dash-nav-item" data-short="Deals"><span className="dash-nav-dot"></span>Applications</div>
                <div className="dash-nav-item" data-short="Offers"><span className="dash-nav-dot"></span>Offers</div>
                <div className="dash-nav-item" data-short="Buyers"><span className="dash-nav-dot"></span>Customers</div>
                <div className="dash-nav-item" data-short="Docs"><span className="dash-nav-dot"></span>Documents</div>
              </aside>

              <div className="dash-main">
                <div className="dash-heading">
                  <div>
                    <h3>Dealer Dashboard</h3>
                    <p>Keep every financing opportunity moving.</p>
                  </div>
                  <span className="dash-new">+ New Application</span>
                </div>

                <div className="dash-stat-grid">
                  <div className="dash-stat">
                    <span>Active Deals</span>
                    <strong>18</strong>
                    <em>↑ 4 this week</em>
                  </div>
                  <div className="dash-stat">
                    <span>Prequalified</span>
                    <strong>12</strong>
                    <em>Ready to present</em>
                  </div>
                  <div className="dash-stat">
                    <span>Funded Volume</span>
                    <strong>$684K</strong>
                    <em>Current month</em>
                  </div>
                </div>

                <div className="deal-card">
                  <div className="deal-head">
                    <strong>Recent Opportunities</strong>
                    <span>View all</span>
                  </div>
                  <div className="deal-row">
                    <b>Summit Grading LLC</b>
                    <span>$128,000</span>
                    <span className="status">Prequalified</span>
                  </div>
                  <div className="deal-row">
                    <b>Blacktop Transport</b>
                    <span>$84,500</span>
                    <span className="status">Offer Ready</span>
                  </div>
                  <div className="deal-row">
                    <b>Horizon Farms Inc.</b>
                    <span>$212,000</span>
                    <span className="status">Approved</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="floating-card floating-one">
            <span className="float-icon">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <strong>Offer ready</strong>
            <span>Customer options available</span>
          </div>

          <div className="floating-card floating-two">
            <span className="float-icon">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8"/>
              </svg>
            </span>
            <strong>Fast direction</strong>
            <span>Keep the sales conversation alive</span>
          </div>
        </div>
      </div>

      <div className="dd-container proof-strip">
        <div className="proof-grid">
          <div className="proof-item">
            <strong>Trusted by&nbsp;{dealerCount}+</strong>
            <span>Dealers and vendors</span>
          </div>
          <div className="proof-item">
            <strong>1 Form</strong>
            <span>A simpler starting point</span>
          </div>
          <div className="proof-item">
            <strong>Soft Pull</strong>
            <span>Prequalification direction</span>
          </div>
          <div className="proof-item">
            <strong>Real Support</strong>
            <span>People who understand deals</span>
          </div>
        </div>
      </div>
    </section>

    <section className="section platform" id="platform">
      <div className="dd-container">
        <div className="center">
          <span className="section-kicker">Built for the point of sale</span>
          <h2 className="section-heading">Financing should help your sales team close, not slow them down.</h2>
          <p className="section-copy">
            DealerDog gives your team a clean, guided way to start a financing conversation, review possible paths, and move qualified buyers into the full approval process.
          </p>
        </div>

        <div className="feature-grid">
          <article className="feature-card">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 5h16v14H4V5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                <path d="M8 9h8M8 13h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </div>
            <h3>One guided application</h3>
            <p>
              Start with the essential customer and deal information your financing team needs, without burying the buyer in unnecessary steps.
            </p>
            <a className="feature-link" href="#how-it-works">
              See the process
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m9 18 6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </a>
          </article>

          <article className="feature-card">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 17V9M12 17V5M19 17v-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M3 20h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </div>
            <h3>Clear financing direction</h3>
            <p>
              Review prequalification guidance and viable deal structures while your customer is engaged and ready to discuss payments.
            </p>
            <a className="feature-link" href="#benefits">
              Explore the benefits
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m9 18 6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </a>
          </article>

          <article className="feature-card">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M7 12.5 10 15l7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z" stroke="currentColor" strokeWidth="1.8"/>
              </svg>
            </div>
            <h3>Support through funding</h3>
            <p>
              When a deal is ready to move, DealerDog and the TrueCore team help structure, package, submit, and guide it toward funding.
            </p>
            <a className="feature-link" href="#contact">
              Talk with the team
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m9 18 6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </a>
          </article>
        </div>
      </div>
    </section>

    <section className="section process" id="how-it-works">
      <div className="dd-container">
        <div className="center">
          <span className="section-kicker">Three simple steps</span>
          <h2 className="section-heading">From interested buyer to a real financing path.</h2>
          <p className="section-copy">
            Give your sales team a repeatable process that keeps the customer informed and the deal moving.
          </p>
        </div>

        <div className="step-grid">
          <article className="step-card">
            <div className="step-number">01</div>
            <h3>Submit the opportunity</h3>
            <p>
              Enter the buyer, business, equipment, and requested structure through one straightforward dealer workflow.
            </p>
          </article>

          <article className="step-card">
            <div className="step-number">02</div>
            <h3>Review possible options</h3>
            <p>
              Receive prequalification direction and identify a workable payment, term, down payment, or documentation path.
            </p>
          </article>

          <article className="step-card">
            <div className="step-number">03</div>
            <h3>Move toward funding</h3>
            <p>
              Present the path to your customer, select the best fit, and let the financing team help carry the deal through closing.
            </p>
          </article>
        </div>
      </div>
    </section>

    <section className="section results" id="benefits">
      <div className="dd-container results-grid">
        <div>
          <span className="section-kicker">Keep the deal alive</span>
          <h2 className="section-heading">Stop losing buyers in the financing gap.</h2>
          <p className="section-copy">
            The longer a customer waits for direction, the more likely they are to delay, shop elsewhere, or disappear. DealerDog helps your team address financing earlier and with more confidence.
          </p>

          <div className="benefit-list">
            <div className="benefit-item">
              <span className="benefit-check">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
                </svg>
              </span>
              Discuss affordability while purchase intent is highest
            </div>
            <div className="benefit-item">
              <span className="benefit-check">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
                </svg>
              </span>
              Give salespeople a consistent financing workflow
            </div>
            <div className="benefit-item">
              <span className="benefit-check">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
                </svg>
              </span>
              Reduce back-and-forth and unproductive guessing
            </div>
            <div className="benefit-item">
              <span className="benefit-check">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
                </svg>
              </span>
              Keep your team focused on selling equipment
            </div>
          </div>

          <a className="button button-primary" href={CALENDLY_URL}>See DealerDog in Action</a>
        </div>

        <div className="comparison-shell" aria-label="Traditional financing compared with DealerDog">
          <div className="comparison-head">
            <div>The old way</div>
            <div>With DealerDog</div>
          </div>

          <div className="comparison-row">
            <div className="comparison-cell">
              <span className="comparison-symbol">×</span>
              Wait hours or days for direction
            </div>
            <div className="comparison-cell">
              <span className="comparison-symbol">✓</span>
              Start the conversation earlier
            </div>
          </div>

          <div className="comparison-row">
            <div className="comparison-cell">
              <span className="comparison-symbol">×</span>
              Guess whether a buyer qualifies
            </div>
            <div className="comparison-cell">
              <span className="comparison-symbol">✓</span>
              Review a clearer possible path
            </div>
          </div>

          <div className="comparison-row">
            <div className="comparison-cell">
              <span className="comparison-symbol">×</span>
              Avoid the monthly payment discussion
            </div>
            <div className="comparison-cell">
              <span className="comparison-symbol">✓</span>
              Discuss structure with confidence
            </div>
          </div>

          <div className="comparison-row">
            <div className="comparison-cell">
              <span className="comparison-symbol">×</span>
              Lose momentum after the quote
            </div>
            <div className="comparison-cell">
              <span className="comparison-symbol">✓</span>
              Keep the buyer moving forward
            </div>
          </div>

          <div className="comparison-row">
            <div className="comparison-cell">
              <span className="comparison-symbol">×</span>
              Navigate financing alone
            </div>
            <div className="comparison-cell">
              <span className="comparison-symbol">✓</span>
              Get help structuring the deal
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="section" id="industries">
      <div className="dd-container industry-wrap">
        <div>
          <span className="section-kicker">Built for equipment sales</span>
          <h2 className="section-heading">Flexible enough for the customers and equipment you sell.</h2>
          <p className="section-copy">
            DealerDog is designed for commercial equipment transactions across industries where speed, deal structure, and customer guidance can make the difference between a quote and a sale.
          </p>
          <div className="button-row" style={{ marginTop: 31 }}>
            <SignupLink className="button button-primary">Start Using DealerDog</SignupLink>
            <a className="button button-secondary" href="#contact">Ask a Question</a>
          </div>
        </div>

        <div className="industry-cloud" aria-label="Industries served">
          <span className="industry-tag">Construction</span>
          <span className="industry-tag">Transportation</span>
          <span className="industry-tag">Agriculture</span>
          <span className="industry-tag">Trailers</span>
          <span className="industry-tag">Vocational Trucks</span>
          <span className="industry-tag">Manufacturing</span>
          <span className="industry-tag">Forestry</span>
          <span className="industry-tag">Material Handling</span>
          <span className="industry-tag">Yellow Iron</span>
          <span className="industry-tag">Specialty Equipment</span>
          <span className="industry-tag">Service Businesses</span>
          <span className="industry-tag">Commercial Vehicles</span>
        </div>
      </div>
    </section>

    <section className="section faq" id="faq">
      <div className="dd-container faq-layout">
        <div>
          <span className="section-kicker">Frequently asked questions</span>
          <h2 className="section-heading">The answers your dealership needs.</h2>
          <p className="section-copy">
            DealerDog is meant to make financing easier for sales teams, not turn them into underwriters.
          </p>
        </div>

        <div className="faq-list">
          <details>
            <summary>Do dealers need financing experience?</summary>
            <div className="faq-answer">
              No. The platform guides your team through the starting information, and the financing team helps review, structure, and move qualified opportunities forward.
            </div>
          </details>

          <details>
            <summary>Does prequalification affect the customer’s credit?</summary>
            <div className="faq-answer">
              DealerDog’s initial prequalification workflow is designed around a soft credit inquiry. A hard inquiry may be required later if the customer chooses to proceed with a full lender submission.
            </div>
          </details>

          <details>
            <summary>What information does the customer provide?</summary>
            <div className="faq-answer">
              The starting application typically includes basic business, ownership, equipment, requested financing, and contact information. Additional documents depend on the selected financing path.
            </div>
          </details>

          <details>
            <summary>Can DealerDog support different credit profiles?</summary>
            <div className="faq-answer">
              DealerDog is built to help identify viable financing paths across a broad range of commercial borrowers and equipment types. Final options remain subject to lender requirements and full underwriting.
            </div>
          </details>

          <details>
            <summary>How does my dealership get started?</summary>
            <div className="faq-answer">
              <SignupLink className="faq-signup-link">Create an account</SignupLink>
              {" "}
              or
              {" "}
              <a href={CALENDLY_URL} className="faq-calendly-link">
                schedule a walkthrough
              </a>
              . The DealerDog team can help configure your dealership, explain the workflow, and show
              your salespeople how to begin submitting opportunities.
            </div>
          </details>
        </div>
      </div>
    </section>

    <section className="cta-section" id="contact">
      <div className="dd-container">
        <div className="cta-panel">
          <div>
            <h2>Ready to make financing part of your sales process?</h2>
            <p>
              Join {dealerCount}+ dealers and vendors using DealerDog to give customers clearer financing direction and keep more equipment opportunities moving.
            </p>
          </div>

          <div className="button-row">
            <SignupLink className="button button-primary">Create an Account</SignupLink>
            <a className="button button-secondary" href={CALENDLY_URL}>Schedule a Demo</a>
          </div>
        </div>
      </div>
    </section>
  
      <footer className="dd-site-footer">

    <div className="dd-container">
      <div className="footer-grid">
        <div>
          <Link className="brand footer-brand" to="/">
            <DealerDogBrand />
          </Link>
          <p className="footer-copy">
            Dealer-first commercial equipment financing built to help sales teams move faster, communicate clearly, and close more deals.
          </p>
        </div>

        <div className="footer-column">
          <h3>Explore</h3>
          <div className="footer-links">
            <a href="#platform">Platform</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#benefits">Benefits</a>
            <a href="#industries">Industries</a>
            <a href="/articles">Insights</a>
            <a href="/tradeshows">Events</a>
          </div>
        </div>

        <div className="footer-column">
          <h3>Get Started</h3>
          <div className="footer-links">
            <SignupLink className="footer-signup-link">Create an Account</SignupLink>
            <a href={CALENDLY_URL}>Schedule a Demo</a>
            <Link to="/demo">Watch the Demo</Link>
            <a href="/articles">Insights</a>
          </div>
        </div>

        <div className="footer-column">
          <h3>More Questions?</h3>
          <div className="footer-links">
            <a href="tel:+18059158581">805-915-8581</a>
            <a href="mailto:andrew@dealerdog.co">andrew@dealerdog.co</a>
            <a href="#faq">Frequently Asked Questions</a>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <span>© {year} DealerDog. All rights reserved.</span>
        <div className="legal-links">
          <Link to="/privacy">Privacy Policy</Link>
          <a href="mailto:andrew@dealerdog.co">Contact DealerDog</a>
        </div>
      </div>
    </div>
  
      </footer>
<div className="mobile-bottom-bar" aria-label="Quick actions">
    <a href={CALENDLY_URL}>Book Demo</a>
    <SignupLink className="footer-signup-link">Create Account</SignupLink>
  </div>
    </>
  );
}
